/**
 * Performance Promo — the single source of the active promo code on the client.
 *
 * There is exactly one representation of a promo code end to end: the raw code. It is the form
 * field, the `?promo_code=` URL trigger, the cookie, and the `?promo=` arg on the performances
 * endpoint. Nothing is encoded — the code is already public in every /promo/{code} ticket link the
 * page renders, so obfuscating the cookie bought nothing but a round trip to get wrong.
 *
 * Split from performance-data.js so body links (performance-promo-links.js) can resolve a code on
 * marketing pages without pulling in the fetch/parse/format helpers they have no use for.
 *
 * Exposed on window.sitchco.performance rather than imported: the build wraps each entry point in
 * an IIFE and enqueues it as a classic script, so a cross-entry ES import would be emitted as a
 * shared chunk the browser never loads. Shared code travels as a handle plus a namespace entry,
 * declared ahead of its consumers in block.json.
 */

/**
 * The two hooks the blocks, the promo form, and the body links coordinate over.
 *
 * PROMO_CHANGE is fired after the active code changes; every block re-fetches and the body links
 * re-apply. DATA_LOAD is fired by each block once it accepts a fetch result as current; the promo
 * form listens and hydrates its own state from it.
 *
 * A DATA_LOAD handler must never fire PROMO_CHANGE — that is the loop guard. PROMO_CLEARED exists
 * because of it: clearCode() runs from a DATA_LOAD handler (the endpoint reported the code invalid),
 * so it can't announce itself as a promo change, yet the body links have no other way to hear that
 * the code they just wrote into every href is dead. Only promo-links subscribes, and it issues no
 * fetch, so there is no path back into DATA_LOAD.
 */
const HOOKS = {
    PROMO_CHANGE: 'performance-promo-change',
    PROMO_CLEARED: 'performance-promo-cleared',
    DATA_LOAD: 'performance-data-load',
};

/**
 * Read lazily, never destructured at module scope: ModuleAssets::inlineScriptData() prints before
 * the handle in production, but the dev server echoes it on wp_head priority 10 — after head
 * scripts have already printed. Everything below reaches config through this, and bootstrap() is
 * deferred to first use for the same reason.
 */
function restConfig() {
    return window.sitchco?.performanceRest ?? {};
}

/**
 * How long to wait before treating a request as failed.
 *
 * Without a ceiling, a connection that opens and then stalls never settles the promise. A block is
 * left with aria-busy="true" and a spinner forever — no error, no retry — and the promo form's
 * in-flight guard is never released, so every later submit is silently dropped with no recovery
 * short of a page reload. Aborting hands the existing failure paths something to show.
 */
const REQUEST_TIMEOUT_MS = 30000;

/**
 * The code the client believes is active, once the URL trigger or a POST has told us. null means
 * "nothing has overridden the cookie this page load".
 */
let codeOverride = null;

/** Resolves once the code is settled — i.e. once any `?promo_code=` has been validated. */
let settled = null;

/**
 * Bumped by every code submission. A response writes client state only while its own POST is still
 * the newest, so the URL trigger's background validation can't restore a code the visitor removed
 * while it was in flight.
 */
let revision = 0;

/** The URL trigger's background POST, while it is still cancellable. */
let bootstrapRequest = null;

/**
 * The characters a promo code may contain, mirroring PromoCodeRepository::normalizeCode().
 *
 * The two have to agree on what "no usable code" means. The server's sanitize_callback strips a
 * code to these characters, so `?promo_code=%20` arrived as a space and left as '' — which
 * submitPromoCode() reads as an explicit removal, expiring a valid cookie. Normalizing here means
 * a trigger with nothing usable in it is simply not a trigger, and the route rejects the case this
 * misses rather than treating it as a clear.
 */
const CODE_PATTERN = /[^\w-]/g;
const CODE_MAX_LENGTH = 64;

function normalizeCode(value) {
    return String(value ?? '')
        .replace(CODE_PATTERN, '')
        .slice(0, CODE_MAX_LENGTH);
}

/** RegExp-escape, so a cookie name is never interpreted as a pattern. */
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function readCookie(name) {
    if (!name) {
        return '';
    }

    // `;\s*` rather than `(^| )`: a bare space also matches inside another cookie's value when that
    // value happens to contain one.
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escapeRegExp(name)}=([^;]*)`));
    if (!match) {
        return '';
    }

    try {
        // PHP's setcookie() percent-encodes the value and document.cookie returns it raw. (Non-raw
        // setcookie() is urlencode(), which spells a space `+` where decodeURIComponent expects
        // `%20` — reachable only if Criterion ever echoed a code outside [\w-], which would produce
        // a broken /promo/{code} ticket link long before it surfaced here.)
        return decodeURIComponent(match[1]);
    } catch {
        // Nothing this system writes can be malformed, but an extension, another same-origin
        // script, or a hand-edited jar can be. That matters out of proportion to its likelihood:
        // this runs inside a REGISTER handler, @wordpress/hooks invokes handlers with no catch, and
        // sitchco-core's main.js calls doAction(INIT/REGISTER/READY) as three plain statements — so
        // an escaped throw would take the sitewide READY action down with it.
        return '';
    }
}

/**
 * fetch + parse under one timeout, shared by every request the performance scripts make.
 *
 * The ceiling has to cover the body read, not just the headers: fetch() settles as soon as headers
 * arrive, so a server that then stalls the body would leave response.json() pending with nothing
 * left to abort it. Lives in this file rather than performance-data.js because block.json declares
 * that script as depending on this one, and the dependency cannot run the other way.
 */
async function fetchJson(url, { signal = null, label = 'Request', ...options } = {}) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    const timer = setTimeout(abort, REQUEST_TIMEOUT_MS);
    signal?.addEventListener('abort', abort);

    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal,
        });
        if (!response.ok) {
            throw new Error(`${label} failed: ${response.status}`);
        }

        try {
            return await response.json();
        } catch (error) {
            // Flagged because the failure is not a no-op. The response's headers already reached
            // the browser and any Set-Cookie in them has been applied, so a caller cannot assume
            // its request left no trace — a removal whose body read is aborted has genuinely
            // cleared the cookie, whatever the form still shows.
            error.afterHeaders = true;
            throw error;
        }
    } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
    }
}

function expireCookie(name) {
    if (name) {
        document.cookie = `${name}=; Max-Age=0; path=/`;
    }
}

/** Drop a param from the address bar without navigating, so a refresh doesn't re-trigger it. */
function stripParam(name) {
    const url = new URL(window.location.href);
    if (!name || !url.searchParams.has(name)) {
        return;
    }

    url.searchParams.delete(name);
    window.history.replaceState(window.history.state, '', url);
}

/**
 * Resolve the `?promo_code=` trigger, retire the pre-SP-612 cookie, and settle the active code.
 *
 * The trigger is applied optimistically: the code is adopted synchronously so the blocks' very
 * first fetch already carries it (no flash of un-promoted pricing), and the POST that follows is
 * what persists the cookie for subsequent navigations. The GET the blocks issue validates the code
 * independently, so the POST is only load-bearing for persistence and for pages with no block.
 */
function bootstrap() {
    if (settled) {
        return;
    }

    const { promoParam, legacyPromoParam } = restConfig();
    // Pre-SP-612 visitors carry an obfuscated value under the old name. Left alone it would be
    // sent verbatim as a promo code forever.
    expireCookie(legacyPromoParam);

    // Read before the URL code is adopted below: this is the code the visitor keeps if the trigger
    // turns out not to resolve.
    const existing = readCookie(promoParam);
    const params = promoParam ? new URL(window.location.href).searchParams : null;
    // Normalized before it is judged truthy. `?promo_code=%20`, `+`, or `!` all arrive non-empty
    // but carry no usable code, and POSTing one made the server sanitize it to '' — which it reads
    // as "clear the promo", expiring a working cookie. A trigger with nothing in it is not a trigger.
    const fromUrl = normalizeCode(params?.get(promoParam));
    // Stripped whenever the param is present, not only when it carried a value. A bare
    // `?promo_code=` is falsy and takes the cookie branch below — treating it as "clear the promo"
    // would let one broken CMS link wipe a valid code — but it would otherwise sit in the address
    // bar for the rest of the visit and survive a refresh.
    if (params?.has(promoParam)) {
        stripParam(promoParam);
    }
    if (!fromUrl) {
        // Resolved from the cookie directly rather than through getCode(), which would call back
        // into bootstrap() before `settled` is assigned and recurse.
        settled = Promise.resolve(codeOverride ?? existing);
        return;
    }

    // Adopted optimistically so the blocks' very first fetch already carries it, then validated by
    // the POST below — which is also what persists the cookie for subsequent navigations.
    codeOverride = fromUrl;
    bootstrapRequest = new AbortController();
    const mine = ++revision;
    settled = postCode(fromUrl, bootstrapRequest.signal)
        .then((result) => {
            if (mine !== revision) {
                return getCode();
            }

            applyResult(result);

            if (!result.valid) {
                // Reverted to null — "no client-side opinion, defer to the cookie" — rather than
                // '': a campaign link with a typo'd code must not destroy a working code the
                // visitor already had. Announced so the blocks re-price at the surviving code;
                // legal from here because this is the POST's own continuation, not a DATA_LOAD
                // handler, so the loop guard documented above still holds.
                codeOverride = null;

                if (existing) {
                    window.sitchco.hooks.doAction(HOOKS.PROMO_CHANGE, {
                        promo: null,
                        code: existing,
                        cleared: false,
                    });
                }
            }
            return getCode();
        })
        .catch(() => getCode())
        .finally(() => {
            bootstrapRequest = null;
        });
}

/** The active promo code, or '' — resolved synchronously from the URL trigger or the cookie. */
function getCode() {
    bootstrap();

    if (codeOverride !== null) {
        return codeOverride;
    }
    return readCookie(restConfig().promoParam);
}

/**
 * The active code once any pending validation has finished.
 *
 * Consumers that have no second chance to notice an invalid code — body links — should await this.
 * The blocks don't need to: their own GET reports promoInvalid.
 */
function ready() {
    bootstrap();
    return settled;
}

/**
 * Record the code the client should use from here on.
 *
 * '' means "explicitly no code"; null means "no client-side opinion, defer to the cookie again" —
 * the two states codeOverride is documented to distinguish.
 */
function setCode(code) {
    codeOverride = code ?? null;
}

/**
 * Drop the code the server has just reported it can no longer resolve.
 *
 * The cookie survives one case: the verdict is about a *different* code than the one in the jar —
 * a `?promo_code=` trigger, adopted optimistically, that turned out not to resolve. Following a
 * link with a typo'd code must not destroy a working code the visitor already had. It also makes a
 * page carrying a block agree with a marketing page carrying only body links, where no GET ever
 * runs and nothing would have cleared it either way.
 *
 * Decided from the code the verdict concerns, passed in by the caller, rather than from any
 * lingering record of the trigger. Deciding it from a module-level `fromUrlCode` meant an
 * already-resolved trigger went on shielding the jar for the rest of the page view: fall back to a
 * dead cookie after an invalid trigger and its own promoInvalid could never expire it, so the dead
 * code kept being written into every body link. It also keeps a second call a no-op — two blocks
 * on one page both report promoInvalid about the same code, and the second reads the same
 * comparison the first did rather than the state the first left behind.
 *
 * @param {string|null} about The code the server judged, or null when the caller doesn't know.
 */
function clearCode(about = null) {
    const name = restConfig().promoParam;
    const stored = readCookie(name);
    const aboutAnotherCode = Boolean(about) && Boolean(stored) && about !== stored;
    if (!aboutAnotherCode) {
        expireCookie(name);
    }

    // null — "no client-side opinion" — hands getCode() back to the surviving cookie.
    codeOverride = aboutAnotherCode ? null : '';

    // Carries whatever is active *after* the clear: '' in the ordinary case, the surviving code in
    // the URL-trigger one. promo-links re-applies either correctly.
    window.sitchco.hooks.doAction(HOOKS.PROMO_CLEARED, { code: getCode() });
}

/**
 * POST a code and hand back the parsed body. No client state is written — that is the caller's,
 * because only the caller knows whether its request is still the newest.
 *
 * credentials: 'same-origin' is required — PromoCodeRepository::add()/remove() answer with the
 * Set-Cookie, and remove() only emits it when the browser sent the cookie in the first place.
 */
function postCode(code, signal = null) {
    const endpoint = restConfig().promoCode;
    if (!endpoint) {
        return Promise.reject(new Error('Promo code endpoint is not configured.'));
    }
    return fetchJson(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        label: 'Promo code request',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify({ code }),
        signal,
    });
}

/** The server's spelling of the code wins, so the cookie and the ticket URLs agree on case. */
function applyResult(result) {
    if (result.valid || result.cleared) {
        setCode(result.promo?.code ?? '');
    }
}

/**
 * Submit a promo code for validation. An empty string clears the active code.
 *
 * Any background validation the `?promo_code=` trigger started is cancelled first. A client-side
 * guard alone would not be enough: that POST's Set-Cookie is written server-side by
 * PromoCodeRepository::save(), so a late success would restore a code the visitor just removed and
 * the next navigation would read it straight back off the cookie. The request itself has to die.
 */
async function submit(code) {
    bootstrapRequest?.abort();
    const mine = ++revision;
    const result = await postCode(code);
    if (mine !== revision) {
        // Superseded. Returning this response anyway is how two forms on one page — both blocks
        // include templates/promo-code.twig — ended up disagreeing: the guard suppressed the
        // loser's applyResult(), but the caller still rendered the payload and fired PROMO_CHANGE
        // with it, so the cookie held one code while a form and the body links showed another.
        // The loser reports the state that won instead of its own.
        return {
            valid: Boolean(getCode()),
            cleared: false,
            promo: null,
            superseded: true,
        };
    }

    applyResult(result);
    return result;
}

/**
 * Fill in a `data-promo-link` template with the active code.
 *
 * The template is the promo-ready form of the anchor's own URL, built server-side by
 * PromoCode::applyCodeToUrl() with a placeholder — so the /promo/{code} path convention lives in
 * PHP only and the client never parses a URL. Returns '' when there is nothing to apply.
 */
function applyToTemplate(template, code) {
    const placeholder = restConfig().promoPlaceholder;
    if (!template || !code || !placeholder) {
        return '';
    }
    return template.replaceAll(placeholder, encodeURIComponent(code.toLowerCase()));
}

window.sitchco.performance = Object.assign(window.sitchco.performance || {}, {
    hooks: HOOKS,
    promo: {
        applyToTemplate,
        clearCode,
        fetchJson,
        getCode,
        ready,
        restConfig,
        setCode,
        submit,
    },
});

// Bootstrapped here rather than left to the first getCode()/ready() caller: this script is enqueued
// on every front-end page, and on a page with no block and no marked link there is no such caller —
// so a `?promo_code=` landing URL would never be adopted and the legacy cookie never retired. This
// is the work PerformanceModule's removed template_redirect handler did for every page.
// Inside register() because bootstrap() reads the inline config, which the dev server prints on
// wp_head after head scripts have already run.
window.sitchco.register(bootstrap);
